import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import Header from "../../components/ui/Header";
import Footer from "../../components/ui/Footer";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import CaseStudyCard from "./components/CaseStudyCard";
import FilterSection from "./components/FilterSection";
import StatsSection from "./components/StatsSection";
import TestimonialSection from "./components/TestimonialSection";
import PartnerShowcase from "./components/PartnerShowcase";
import CTASection from "./components/CTASection";

const CaseStudies = () => {
  const [selectedIndustry, setSelectedIndustry] = useState("all");
  const [selectedService, setSelectedService] = useState("all");
  const [selectedSize, setSelectedSize] = useState("all");

  const industries = [
    { value: "all", label: "All Industries" },
    { value: "healthcare", label: "Healthcare" },
    { value: "saas", label: "SaaS" },
    { value: "technology", label: "Technology" },
    { value: "manufacturing", label: "Manufacturing" },
    { value: "education", label: "Education" },
  ];

  const services = [
    { value: "all", label: "All Services" },
    { value: "cloud", label: "Cloud Migration" },
    { value: "cybersecurity", label: "Cybersecurity" },
    { value: "branding", label: "Brand Development" },
    { value: "infrastructure", label: "IT Infrastructure" },
    { value: "digital", label: "Digital Transformation" },
  ];

  const businessSizes = [
    { value: "all", label: "All Sizes" },
    { value: "startup", label: "Startup" },
    { value: "mid", label: "Mid-Market" },
    { value: "enterprise", label: "Enterprise" },
  ];

  const caseStudies = [
    {
      id: 1,
      title: "Healthcare Cloud Transformation with Enhanced Security",
      description:
        "Complete migration of legacy healthcare systems to Azure cloud with HIPAA-compliant security implementation and brand redesign for modern patient experience.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_1a7cf43e4-1764670836798.png",
      imageAlt:
        "Modern hospital interior with advanced medical technology equipment and digital screens showing patient data in bright clean environment",
      industry: "healthcare",
      service: "cloud",
      businessSize: "enterprise",
      clientName: "HealthTech Solutions",
      clientLogo: "https://via.placeholder.com/100x100/0d6efd/ffffff?text=HTS",
      clientLogoAlt:
        "HealthTech Solutions company logo with blue medical cross symbol on white background",
      duration: "8 months",
      metrics: [
        { value: "40%", label: "Cost Reduction" },
        { value: "99.9%", label: "Uptime" },
        { value: "3x", label: "Performance" },
      ],
    },
    {
      id: 2,
      title: "SaaS Platform Launch with Complete Brand Identity",
      description:
        "End-to-end development of educational SaaS platform with integrated brand strategy, including logo design, marketing materials, and digital presence.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_17b542c8d-1764670082756.png",
      imageAlt:
        "Modern office workspace with diverse team collaborating around laptop displaying colorful SaaS dashboard interface in bright natural lighting",
      industry: "saas",
      service: "digital",
      businessSize: "startup",
      clientName: "EduTech Innovations",
      clientLogo: "https://via.placeholder.com/100x100/28a745/ffffff?text=ETI",
      clientLogoAlt:
        "EduTech Innovations company logo with green graduation cap icon on white background",
      duration: "6 months",
      metrics: [
        { value: "3x", label: "User Growth" },
        { value: "250%", label: "Revenue ↑" },
        { value: "4.8/5", label: "User Rating" },
      ],
    },
    {
      id: 3,
      title: "Manufacturing IT Infrastructure Modernization",
      description:
        "Legacy system upgrade with Microsoft Azure integration, implementing IoT sensors for real-time monitoring and comprehensive corporate rebranding.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_1111c434b-1766488666884.png",
      imageAlt:
        "Industrial manufacturing facility with modern automated machinery and digital monitoring systems in clean well-lit production environment",
      industry: "manufacturing",
      service: "infrastructure",
      businessSize: "enterprise",
      clientName: "Global Manufacturing",
      clientLogo: "https://via.placeholder.com/100x100/fd7e14/ffffff?text=GMS",
      clientLogoAlt:
        "Global Manufacturing Solutions company logo with orange gear symbol on white background",
      duration: "10 months",
      metrics: [
        { value: "60%", label: "Efficiency ↑" },
        { value: "45%", label: "Cost Savings" },
        { value: "Zero", label: "Downtime" },
      ],
    },
    {
      id: 4,
      title: "Cybersecurity Overhaul for Financial Services",
      description:
        "Implementation of enterprise-grade cybersecurity framework with Acronis backup solutions, threat detection systems, and security-focused brand messaging.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_18f6b9080-1764670826754.png",
      imageAlt:
        "Cybersecurity operations center with multiple monitors displaying network security data and threat analysis in dark blue ambient lighting",
      industry: "technology",
      service: "cybersecurity",
      businessSize: "mid",
      clientName: "SecureFinance Corp",
      clientLogo: "https://via.placeholder.com/100x100/6f42c1/ffffff?text=SFC",
      clientLogoAlt:
        "SecureFinance Corp company logo with purple shield and lock icon on white background",
      duration: "5 months",
      metrics: [
        { value: "100%", label: "Threat Block" },
        { value: "Zero", label: "Breaches" },
        { value: "24/7", label: "Monitoring" },
      ],
    },
    {
      id: 5,
      title: "E-Learning Platform Digital Transformation",
      description:
        "Complete digital transformation including cloud infrastructure setup, mobile app development, and cohesive brand identity across all touchpoints.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_100b79617-1764669441684.png",
      imageAlt:
        "Students using tablets and laptops in modern classroom with interactive digital whiteboard displaying educational content",
      industry: "education",
      service: "digital",
      businessSize: "mid",
      clientName: "LearnHub Academy",
      clientLogo: "https://via.placeholder.com/100x100/20c997/ffffff?text=LHA",
      clientLogoAlt:
        "LearnHub Academy company logo with teal book and lightbulb icon on white background",
      duration: "7 months",
      metrics: [
        { value: "5x", label: "Student Base" },
        { value: "92%", label: "Satisfaction" },
        { value: "40%", label: "Cost Down" },
      ],
    },
    {
      id: 6,
      title: "Retail Chain Cloud Migration & Branding",
      description:
        "Multi-location retail chain migration to Zoho ecosystem with POS integration, inventory management, and unified brand experience across stores.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_1241e8e21-1765081387631.png",
      imageAlt:
        "Modern retail store interior with clean white shelving displaying products and digital price tags in bright spacious environment",
      industry: "technology",
      service: "cloud",
      businessSize: "mid",
      clientName: "RetailPro Solutions",
      clientLogo: "https://via.placeholder.com/100x100/dc3545/ffffff?text=RPS",
      clientLogoAlt:
        "RetailPro Solutions company logo with red shopping cart icon on white background",
      duration: "9 months",
      metrics: [
        { value: "35%", label: "Sales ↑" },
        { value: "50%", label: "Faster Ops" },
        { value: "98%", label: "Accuracy" },
      ],
    },
    {
      id: 7,
      title: "Startup Tech Stack & Brand Launch",
      description:
        "Complete technology infrastructure setup for AI startup including cloud architecture, DevOps pipeline, and comprehensive brand identity development.",
      image: "https://images.unsplash.com/photo-1541960071727-c531398e7494",
      imageAlt:
        "Startup office with young professionals working on computers with colorful sticky notes on glass walls in collaborative workspace",
      industry: "technology",
      service: "infrastructure",
      businessSize: "startup",
      clientName: "AI Innovations Lab",
      clientLogo: "https://via.placeholder.com/100x100/ffc107/000000?text=AIL",
      clientLogoAlt:
        "AI Innovations Lab company logo with yellow neural network icon on white background",
      duration: "4 months",
      metrics: [
        { value: "2M+", label: "Funding" },
        { value: "15", label: "Team Size" },
        { value: "3", label: "Products" },
      ],
    },
    {
      id: 8,
      title: "Healthcare Data Security Compliance",
      description:
        "HIPAA compliance implementation with Acronis backup solutions, data encryption, and security-focused brand positioning for patient trust.",
      image:
        "https://img.rocket.new/generatedImages/rocket_gen_img_195a6e902-1764648555298.png",
      imageAlt:
        "Medical professional using secure tablet device with patient data in modern hospital setting with privacy screens",
      industry: "healthcare",
      service: "cybersecurity",
      businessSize: "mid",
      clientName: "MediSecure Systems",
      clientLogo: "https://via.placeholder.com/100x100/0d6efd/ffffff?text=MSS",
      clientLogoAlt:
        "MediSecure Systems company logo with blue medical shield icon on white background",
      duration: "6 months",
      metrics: [
        { value: "100%", label: "Compliant" },
        { value: "Zero", label: "Violations" },
        { value: "A+", label: "Security" },
      ],
    },
    {
      id: 9,
      title: "Enterprise Brand Refresh & IT Upgrade",
      description:
        "Simultaneous IT infrastructure modernization and complete brand refresh for established manufacturing company entering digital markets.",
      image: "https://images.unsplash.com/photo-1716703435453-a7733d600d68",
      imageAlt:
        "Corporate team presenting brand strategy on large screen in modern conference room with glass walls and city view",
      industry: "manufacturing",
      service: "branding",
      businessSize: "enterprise",
      clientName: "Legacy Industries",
      clientLogo: "https://via.placeholder.com/100x100/6f42c1/ffffff?text=LI",
      clientLogoAlt:
        "Legacy Industries company logo with purple industrial gear icon on white background",
      duration: "12 months",
      metrics: [
        { value: "80%", label: "Brand Lift" },
        { value: "55%", label: "Tech Upgrade" },
        { value: "4.9/5", label: "Satisfaction" },
      ],
    },
  ];

  const filteredCaseStudies = useMemo(() => {
    return caseStudies?.filter((study) => {
      const industryMatch =
        selectedIndustry === "all" || study?.industry === selectedIndustry;
      const serviceMatch =
        selectedService === "all" || study?.service === selectedService;
      const sizeMatch =
        selectedSize === "all" || study?.businessSize === selectedSize;
      return industryMatch && serviceMatch && sizeMatch;
    });
  }, [selectedIndustry, selectedService, selectedSize]);

  return (
    <>
      <Helmet>
        <title>Case Studies - Success Stories | Orator Hype</title>
        <meta
          name="description"
          content="Explore our portfolio of 223+ successful projects across healthcare, SaaS, technology, and manufacturing. Real results, measurable impact, lasting partnerships."
        />
        <meta
          name="keywords"
          content="case studies, success stories, IT solutions, brand development, digital transformation, client testimonials"
        />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        <main className="flex-1 pt-20 md:pt-24">
          <section className="bg-gradient-to-br from-primary via-secondary to-accent py-12 md:py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 text-center">
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-4 md:mb-6">
                <Icon name="Award" size={32} color="#ffffff" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6 font-heading">
                Success Stories That Inspire
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-white/90 max-w-3xl mx-auto mb-6 md:mb-8">
                Discover how we've transformed businesses across industries with
                our integrated IT and branding solutions. Real results,
                measurable impact, lasting partnerships.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
                <Button
                  variant="default"
                  size="lg"
                  onClick={() => console.log("Download case studies")}
                  iconName="Download"
                  iconPosition="left"
                  className="bg-white text-primary hover:bg-white/90 w-full sm:w-auto"
                >
                  Download Case Studies
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => console.log("Schedule consultation")}
                  iconName="Calendar"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white/10 w-full sm:w-auto"
                >
                  Schedule Consultation
                </Button>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
            <StatsSection />

            <FilterSection
              selectedIndustry={selectedIndustry}
              setSelectedIndustry={setSelectedIndustry}
              selectedService={selectedService}
              setSelectedService={setSelectedService}
              selectedSize={selectedSize}
              setSelectedSize={setSelectedSize}
              industries={industries}
              services={services}
              businessSizes={businessSizes}
            />

            <div className="mb-6 md:mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
                <div>
                  <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground mb-2 font-heading">
                    Featured Case Studies
                  </h2>
                  <p className="text-sm md:text-base text-text-secondary">
                    Showing {filteredCaseStudies?.length} of{" "}
                    {caseStudies?.length} success stories
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => console.log("View all")}
                  iconName="Grid"
                  iconPosition="left"
                >
                  View All Projects
                </Button>
              </div>
            </div>

            {filteredCaseStudies?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-8 md:mb-12 lg:mb-16">
                {filteredCaseStudies?.map((caseStudy) => (
                  <CaseStudyCard key={caseStudy?.id} caseStudy={caseStudy} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 md:py-16 lg:py-20">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <Icon
                    name="Search"
                    size={32}
                    color="var(--color-text-secondary)"
                  />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3 md:mb-4">
                  No Case Studies Found
                </h3>
                <p className="text-sm md:text-base text-text-secondary mb-6 md:mb-8">
                  Try adjusting your filters to see more results
                </p>
                <Button
                  variant="default"
                  onClick={() => {
                    setSelectedIndustry("all");
                    setSelectedService("all");
                    setSelectedSize("all");
                  }}
                  iconName="RotateCcw"
                  iconPosition="left"
                >
                  Reset Filters
                </Button>
              </div>
            )}

            <TestimonialSection />

            <PartnerShowcase />

            <CTASection />
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CaseStudies;
